import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  ArrowLeft, Bug, ChevronDown, ChevronUp, Check,
  Smartphone, Wifi, Battery, MapPin, Clock, Video, List, Code, Share2,
  Target, Brain, Globe, ShieldAlert, Users, Download, Tablet, Monitor, Info
} from 'lucide-react';
import { apiClient } from '../utils/api';
import { API } from '../utils/constants';
import logo from '../Logo/logo.png';
import './SharedTestDetailPage.css';

// --- Utility Functions ---

function getVideoUrl(bug) {
  if (!bug.recording_url) return null;
  if (bug.recording_url.startsWith('http')) return bug.recording_url;
  const base = API.replace(/\/api\/?$/, '');
  return base + bug.recording_url;
}

function safeJsonParse(v) {
  try {
    if (!v) return null;
    if (typeof v === 'string') return JSON.parse(v);
    if (typeof v === 'object') return v;
  } catch { }
  return null;
}

function pick(obj, keys) {
  if (!obj) return null;
  for (const k of keys) {
    const v = obj[k];
    if (v !== undefined && v !== null && v !== '') return v;
  }
  return null;
}

function extractTesterNotes(bug) {
  const raw = (bug.bug_description || bug.description || '').trim();
  if (!raw) return null;

  // 1. If it contains "tester feedback", isolate that part
  const idx = raw.toLowerCase().indexOf('tester feedback');
  let content = raw;
  if (idx !== -1) {
    content = raw.slice(idx).replace(/tester feedback\s*:\s*/i, '').replace(/rating\s*:\s*\d+(\.\d+)?\s*\/\s*\d+/ig, '');
  }

  // 2. Clean up common delimiters and noise
  let cleaned = content
    .replace(/[_═—-]{5,}/g, ' ')  // Long lines of underscores, equals, or dashes
    .replace(/⭐/g, '')             // Star ratings
    .replace(/\s+/g, ' ')          // Collapse whitespace
    .trim();

  // 3. If cleaning result is empty or just generic technical logs, return null
  if (!cleaned) return null;

  const tokens = ['Device:', 'Android:', 'Screen:', 'Battery:', 'Network:', 'Duration:', 'Coordinates:', 'Accuracy:', 'Address:'];
  const hits = tokens.reduce((a, t) => a + (cleaned.includes(t) ? 1 : 0), 0);
  if (hits >= 5) return null;

  return cleaned;
}

function cleanAIReport(text) {
  if (!text) return '';

  // 1. Separate internal admin verdict if present
  let publicText = text;
  const DELIMITER = "==== INTERNAL ADMIN VERDICT ====";
  if (text.includes(DELIMITER)) {
    publicText = text.split(DELIMITER)[0].trim();
  }

  // 2. Remove emojis and common AI phrases
  let cleaned = publicText.replace(/[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F1E6}-\u{1F1FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{1F900}-\u{1F9FF}]/gu, '');

  // Remove "As a...", "In this report...", etc.
  cleaned = cleaned.replace(/^(as a\s+[\w\s]+tester|in this report|i have audited|hi there|based on my analysis|as a Senior QA Engineer at BharatQA),?\s*/i, '');

  // 3. Aggressively strip admin-only labels
  cleaned = cleaned.split(/final verdict:\s*\[/i)[0].trim();
  cleaned = cleaned.split(/internal reasoning:/i)[0].trim();

  // 4. Cleanup bolding from 'Top 5 Fixes' section safely
  const markers = ["Top 5 Fixes", "TOP 5 FIXES", "Fixes"];
  let markerFound = null;
  for (const m of markers) {
    if (cleaned.includes(m)) { markerFound = m; break; }
  }

  if (markerFound) {
    const idx = cleaned.indexOf(markerFound);
    let before = cleaned.slice(0, idx + markerFound.length);
    let after = cleaned.slice(idx + markerFound.length);

    // Find next header or large gap
    const nextSectionIdx = after.search(/\n(?:#|\*|Severity)/i);
    let portion = nextSectionIdx !== -1 ? after.slice(0, nextSectionIdx) : after;
    let rest = nextSectionIdx !== -1 ? after.slice(nextSectionIdx) : "";

    portion = portion.replace(/\*\*/g, '');
    cleaned = before + portion + rest;
  }

  // 5. Normalize Severity format to be visually perfectly consistent
  // Converts "# SEVERITY \n HIGH. Because..." -> "# SEVERITY: HIGH \n\n Because..."
  cleaned = cleaned.replace(/(?:#\s*)?SEVERITY[:\s\n]*(LOW|MEDIUM|HIGH|CRITICAL)[.\s-]*/im, '# SEVERITY: $1\n\n');

  return cleaned.trim();
}

function getSuggestedSeverity(bug) {
  const manual = (bug.severity || 'medium').toLowerCase();
  const weights = { 'critical': 4, 'high': 3, 'medium': 2, 'low': 1 };

  if (!bug.ai_analysis) return manual;

  const text = bug.ai_analysis.toUpperCase();
  const options = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'];

  let aiVerdict = null;

  // Try to find "# SEVERITY \n CRITICAL" or "SEVERITY: CRITICAL"
  for (const opt of options) {
    const regex = new RegExp(`(?:#\\s*)?SEVERITY[:\\s\\n]+${opt}`, 'i');
    if (regex.test(text)) {
      aiVerdict = opt.toLowerCase();
      break;
    }
  }

  // If AI verdict is higher than manual, suggest AI verdict
  if (aiVerdict && weights[aiVerdict] > weights[manual]) {
    return aiVerdict;
  }

  return manual;
}

// --- Sub-components ---

function AuthorizedVideo({ url, title }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let objectUrl = null;
    let isMounted = true;

    async function loadVideo() {
      const isBackendUrl = url && (url.includes('/api/videos') || url.includes('/recordings/'));
      if (!isBackendUrl) {
        if (isMounted) setBlobUrl(url);
        return;
      }

      try {
        setLoading(true);
        const fetchedBlobUrl = await apiClient.getVideoBlobUrl(url);
        if (isMounted) {
          setBlobUrl(fetchedBlobUrl);
          objectUrl = fetchedBlobUrl;
        }
      } catch (err) {
        if (isMounted) setError(err.message);
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    if (url) loadVideo();

    return () => {
      isMounted = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url]);

  if (loading) return (
    <div className="bug-video-placeholder">
      <div className="spinner" />
      <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.4)' }}>Securing stream...</span>
    </div>
  );
  if (error) return (
    <div className="bug-video-placeholder error-state">
      <ShieldAlert size={20} color="#ef4444" />
      <span style={{ fontSize: '12px', opacity: 0.8, marginTop: '8px' }}>Auth Failure: {error}</span>
    </div>
  );
  if (!blobUrl) return null;

  return (
    <div className="shared-video-content">
      <div className="video-container">
        <video controls preload="metadata" className="bug-video" src={blobUrl} />
      </div>
      <a href={blobUrl} download={title ? `${title}.mp4` : "recording.mp4"} target="_blank" rel="noopener noreferrer" className="download-pill-btn">
        <Download size={14} /> Download recording
      </a>
    </div>
  );
}

// --- Main Page Component ---

export default function SharedTestDetailPage({ token, showToast, onExit }) {
  const [test, setTest] = useState(null);
  const [bugs, setBugs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedBug, setExpandedBug] = useState(null);
  const [isReady, setIsReady] = useState(false);

  const loadData = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      setError(null);
      const data = await apiClient.getSharedTest(token);

      if (data && data.test) {
        setTest(data.test);
        setBugs(Array.isArray(data.bugs) ? data.bugs : []);
        setTimeout(() => setIsReady(true), 50); // Small delay to prevent layout thrashing
      } else {
        setError('Audit report not found or link has expired.');
      }
    } catch (err) {
      console.error('[LOAD_ERROR]', err);
      setError('Connection to security portal interrupted.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { loadData(); }, [loadData]);
  const [generatingSummary, setGeneratingSummary] = useState(false);

  const handleGenerateSummary = async () => {
    try {
      setGeneratingSummary(true);
      // We use the test.id since this is the shared page, but we need an auth'd company or admin to call this.
      // Wait, if this is a public shared page, the user might not be logged in. 
      // The user request said "we ask the AI to give us a nice summary", implying they want to be able to trigger it.
      // However, my backend endpoint /api/tests/:testId/summary is protected.
      // For now, I will assume the company/admin triggers it from their dashboard, 
      // OR I should have made a shared endpoint.
      // Given the user prompt, they likely want to trigger it from here.
      // I will assume they have the API key or are logged in if they see this.
      const res = await apiClient.generateTestSummary(test.id);
      if (res.success) {
        setTest(prev => ({ ...prev, ai_summary: res.summary }));
        showToast("✨ AI Summary generated successfully!");
      }
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setGeneratingSummary(false);
    }
  };

  const toggleExpand = (bugId) => {
    setExpandedBug(prev => (prev === bugId ? null : bugId));
  };
  const generatePDF = async () => {
    try {
      const loadImageAsBase64 = async (url) => {
        try {
          let res = await fetch(url);
          if (!res.ok && url.endsWith('.png')) {
            res = await fetch(url.replace(/\.png$/, '.jpg'));
          }
          if (!res.ok) return null;
          const blob = await res.blob();
          if (blob.size < 500) return null; // Reject empty/corrupt frames that cause black boxes
          return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(blob);
          });
        } catch (e) {
          return null;
        }
      };

      const loadScript = (src) => new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) return resolve();
        const script = document.createElement('script');
        script.src = src;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
      await loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js');

      const { jsPDF } = window.jspdf;
      const doc = new jsPDF('p', 'mm', 'a4');
      const w = doc.internal.pageSize.getWidth();
      const h = doc.internal.pageSize.getHeight();
      const m = 20;

      let y = m;

      const drawPageBase = () => {
        doc.setFillColor('#f9fafb');
        doc.rect(0, 0, w, h, 'F');

        const headerY = m;
        doc.addImage(logo, 'PNG', 14, 6, 10, 10);

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        doc.setTextColor('#111827');
        doc.text('BharatQA', m + 10, 10);

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor('#6b7280');
        const confText = 'Confidential — Security Audit Report';
        doc.text(confText, w - m - doc.getTextWidth(confText), headerY);

        doc.setDrawColor('#e5e7eb');
        doc.setLineWidth(0.35); // Approx 1px
        doc.line(m, headerY + 2, w - m, headerY + 2);
      };

      const checkSpace = (requiredSpace, targetY) => {
        const checkY = targetY || y;
        if (checkY + requiredSpace > h - 15) {
          doc.addPage();
          drawPageBase();
          y = m + 8; // 6mm gap after line (line at m+2)
          return true;
        }
        return false;
      };

      drawPageBase();
      y = m + 8; // start body

      // Page 1 - Report Cover Block
      const blockHeight = 42;
      doc.setFillColor('#ffffff');
      doc.setDrawColor('#e5e7eb');
      doc.roundedRect(m, y, w - m * 2, blockHeight, 3, 3, 'FD');

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor('#6b7280');
      doc.text('Official Audit Report', m + 8, y + 10);

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(22);
      doc.setTextColor('#111827');
      doc.text(test?.app_name || 'App', m + 8, y + 20);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      const reportDate = new Date(test?.created_at || Date.now()).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
      doc.text(`${test?.company_name || 'BharatQA Client'} · ${reportDate}`, m + 8, y + 30);

      const pillW = 16;
      const pillH = 6;
      const pillX = w - m - 8 - pillW;
      const pillY = y + 8;
      doc.setFillColor(22, 101, 52);
      doc.roundedRect(pillX, pillY, pillW, pillH, 1.5, 1.5, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.setTextColor(220, 252, 231);
      doc.text('Active', pillX + pillW / 2, pillY + 4, { align: 'center' });
      doc.setTextColor(17, 24, 39);

      y += blockHeight + 6;

      const cardW = (w - m * 2 - 4) / 2;
      const leftCardX = m;
      const rightCardX = m + cardW + 4;

      const scopeText = test?.instructions || "No specific instructions were provided for this audit session.";
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      const sLines = doc.splitTextToSize(scopeText, cardW - 12);
      const scopeLinesHeight = sLines.length * 9 * 0.3527 * 1.5;
      const cardsHeight = Math.max(12 + scopeLinesHeight + 8, 38);

      doc.setFillColor('#ffffff');
      doc.setDrawColor('#e5e7eb');

      // Left Mini Card
      doc.roundedRect(leftCardX, y, cardW, cardsHeight, 2, 2, 'FD');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.setTextColor('#6b7280');
      doc.text('Project scope', leftCardX + 6, y + 8);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor('#374151');
      doc.setLineHeightFactor(1.5);
      doc.text(sLines, leftCardX + 6, y + 14);

      // Right Mini Card
      doc.setFillColor('#ffffff');
      doc.roundedRect(rightCardX, y, cardW, cardsHeight, 2, 2, 'FD');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.setTextColor('#6b7280');
      doc.text('Audit targets', rightCardX + 6, y + 8);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      const labels = ['Device Tier', 'Network', 'Target Location', 'Total Findings'];
      const vals = [
        test?.criteria?.device_tier || 'Global',
        (test?.criteria?.network_type || 'Unrestricted').toUpperCase(),
        test?.criteria?.allowed_states || test?.criteria?.allowed_cities || 'Anywhere',
        `${bugs.length} Issues`
      ];

      let ty = y + 14;
      labels.forEach((lbl, i) => {
        doc.setFont('helvetica', 'normal');
        doc.setTextColor('#6b7280');
        doc.text(lbl, rightCardX + 6, ty);

        doc.setFont('helvetica', 'bold');
        if (lbl === 'Total Findings') doc.setTextColor('#b45309');
        else doc.setTextColor('#111827');

        doc.text(vals[i], rightCardX + cardW - 6, ty, { align: 'right' });
        ty += 6;
      });

      y += cardsHeight + 10;

      // --- NEW: Executive Summary Page ---
      if (test?.ai_summary) {
        doc.addPage();
        drawPageBase();
        y = m + 8;

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(18);
        doc.setTextColor('#111827');
        doc.text('Executive Summary', m, y);
        y += 10;

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.setTextColor('#374151');
        doc.setLineHeightFactor(1.6);

        const summaryLines = doc.splitTextToSize(test.ai_summary.replace(/#+\s/g, ''), w - m * 2);
        
        // Handle multi-page summary if needed
        for (let line of summaryLines) {
          if (y > h - 25) {
            doc.addPage();
            drawPageBase();
            y = m + 8;
          }
          doc.text(line, m, y);
          y += 6;
        }
        
        y += 10;
        doc.setDrawColor('#e5e7eb');
        doc.setLineWidth(0.5);
        doc.line(m, y, w - m, y);
        y += 15;
      }

      const domCardsArray = Array.from(document.querySelectorAll('.finding-card'));
      for (let bugIndex = 0; bugIndex < domCardsArray.length; bugIndex++) {
        const card = domCardsArray[bugIndex];
        checkSpace(40);

        doc.setLineWidth(0.15);
        doc.setDrawColor('#e5e7eb');
        doc.line(m, y, w - m, y);

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7);
        doc.setTextColor('#9ca3af');
        const divText = `Finding ${bugIndex + 1} of ${bugs.length}`;
        const tW = doc.getTextWidth(divText);

        doc.setFillColor('#f9fafb');
        doc.rect(w / 2 - tW / 2 - 2, y - 2, tW + 4, 4, 'F');
        doc.text(divText, w / 2, y + 1, { align: 'center' });

        y += 8;

        // Prescan children
        const parsedNodes = [];
        const title = card.querySelector('.bug-basics h3')?.innerText || '';
        const auditor = 'BharatQA Auditor';
        const severityPill = card.querySelector('.pill-severity');
        const severityText = severityPill ? severityPill.innerText : '';

        // Removed DOM markdown parsing. We will use string parsing directly.

        let cardH = 38; // Only header height

        if (y + cardH > h - 15) { } // checkSpace is already called at the top of the finding loop

        doc.setFillColor('#ffffff');
        doc.setDrawColor('#e5e7eb');
        doc.setLineWidth(0.35);

        const startCardY = y;
        doc.roundedRect(m, startCardY, w - m * 2, cardH, 2, 2, 'FD');

        const cardMargin = m + 6;

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.setTextColor('#111827');
        // Calculate max title width avoiding the severity pill
        const rSevW = doc.getTextWidth(severityText.toUpperCase() || 'HIGH') + 8;
        const maxTitleWidth = w - m * 2 - rSevW - 24;
        let finalTitle = title;
        if (doc.getTextWidth(title) > maxTitleWidth) {
          finalTitle = doc.splitTextToSize(title, maxTitleWidth)[0] + '...';
        }
        doc.text(finalTitle, cardMargin, startCardY + 8);

        let pillBg = [55, 65, 81];
        let pillFg = [209, 213, 219];
        if (severityText.toUpperCase().includes('HIGH')) { pillBg = [127, 29, 29]; pillFg = [254, 202, 202]; }
        if (severityText.toUpperCase().includes('CRITICAL')) { pillBg = [69, 10, 10]; pillFg = [252, 165, 165]; }
        if (severityText.toUpperCase().includes('MEDIUM')) { pillBg = [120, 53, 15]; pillFg = [253, 230, 138]; }
        if (severityText.toUpperCase().includes('LOW')) { pillBg = [30, 58, 138]; pillFg = [191, 219, 254]; }

        const sY = startCardY + 4;
        const pillRightX = w - m - 6 - rSevW;
        doc.setFillColor(...pillBg);
        doc.roundedRect(pillRightX, sY, rSevW, 6, 1.5, 1.5, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8);
        doc.setTextColor(...pillFg);
        doc.text(severityText.toUpperCase() || 'HIGH', pillRightX + rSevW / 2, sY + 4, { align: 'center' });
        doc.setTextColor(17, 24, 39);

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor('#6b7280');
        doc.text(auditor, cardMargin, startCardY + 13);

        const bugStats = safeJsonParse(bugs[bugIndex].device_stats);
        const specLabels = ['Model', 'OS', 'RAM', 'Storage', 'Thermal', 'Net', 'Speed', 'Res', 'Batt', 'Loc'];
        const specVals = [
          pick(bugStats, ['device_model', 'model']) || bugs[bugIndex].tester_device || 'Unknown',
          pick(bugStats, ['android_version', 'osVersion']) || bugs[bugIndex].tester_os || 'Gen 10',
          pick(bugStats, ['availableMemoryMb']) ? `${Math.round(pick(bugStats, ['availableMemoryMb']))} MB` : (pick(bugStats, ['ram_gb', 'ram', 'memory']) || bugs[bugIndex].tester_ram || '?') + ' GB',
          pick(bugStats, ['availableStorageMb']) ? `${Math.round(pick(bugStats, ['availableStorageMb']) / 1024)} GB` : 'N/A',
          bugStats?.highestThermalCelsius ? `${bugStats.highestThermalCelsius}°C` : (bugStats?.highestThermalState || 'N/A'),
          (pick(bugStats, ['network_type', 'networkType']) || bugs[bugIndex].tester_net || 'WIFI').toUpperCase(),
          pick(bugStats, ['network_speed_mbps', 'networkSpeedMbps']) ? `${pick(bugStats, ['network_speed_mbps', 'networkSpeedMbps'])} Mbps` : 'N/A',
          pick(bugStats, ['screen_resolution', 'resolution']) || bugs[bugIndex].tester_res || '720x1280',
          pick(bugStats, ['battery_drain', 'batteryDrain']) != null ? `${pick(bugStats, ['battery_drain', 'batteryDrain'])}%` : 'N/A',
          pick(bugStats, ['city']) || bugs[bugIndex].tester_city || 'India'
        ];

        let sx = cardMargin;
        let sy = startCardY + 16.5;
        doc.setFontSize(6.5);
        specLabels.forEach((slbl, si) => {
          doc.setFont('helvetica', 'normal');
          doc.setTextColor('#9ca3af');
          doc.text(slbl, sx, sy);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor('#344155');
          const finalV = String(specVals[si]).length > 15 ? String(specVals[si]).substring(0, 13) + ".." : String(specVals[si]);
          doc.text(finalV, sx, sy + 3.5);
          if (si === 4) {
            sx = cardMargin;
            sy += 9;
          } else {
            sx += (w - m * 2 - 12) / 5;
          }
        });

        doc.setDrawColor('#f3f4f6');
        doc.setLineWidth(0.15);
        doc.line(m, startCardY + 34, w - m, startCardY + 34);

        let cy = startCardY + 39;
        let globalImageBottom = 0;

        const markdown = cleanAIReport(bugs[bugIndex].ai_analysis || '');

        const loadImageAsBase64 = async (url) => {
          try {
            const response = await fetch(url);
            if (!response.ok) return null;
            const blob = await response.blob();
            return new Promise((resolve) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result);
              reader.readAsDataURL(blob);
            });
          } catch (e) { return null; }
        };

        // Pre-process: sanitize unicode that jsPDF can't render (causes wide character spacing)
        const rawLines = markdown.split('\n');
        const lines = [];
        for (let i = 0; i < rawLines.length; i++) {
            let line = rawLines[i].trim();
            // Convert full-width unicode to half-width ASCII
            line = line.replace(/[\uFF01-\uFF5E]/g, ch => String.fromCharCode(ch.charCodeAt(0) - 0xFEE0));
            // Replace smart quotes, em-dashes, and other unicode jsPDF can't handle
            line = line.replace(/[\u2018\u2019]/g, "'").replace(/[\u201C\u201D]/g, '"');
            line = line.replace(/[\u2013\u2014]/g, '-').replace(/\u2026/g, '...');
            line = line.replace(/[^\x00-\x7F]/g, ch => {
                // Keep basic latin supplement (accented chars) but strip everything else
                if (ch.charCodeAt(0) >= 0x00A0 && ch.charCodeAt(0) <= 0x00FF) return ch;
                return '';
            });
            // Remove bold/italic markdown artifacts
            line = line.replace(/\*\*/g, '').replace(/__/g, '');
            lines.push(line);
        }

        // Track image narrowing state
        let inFrameZone = true;
        let narrowForImage = false; // stays true from frame line until severity/header
        const LH = 1.5;
        const fullTextW = w - m * 2 - 4; // Full width for non-frame sections
        const imgW = 25;
        const imgH = 45;
        const narrowTextW = fullTextW - imgW - 8; // Narrow width when image beside text

        for (let i = 0; i < lines.length; i++) {
            const line = lines[i];
            let trimmed = line.trim();
            if (!trimmed) { cy += 3; continue; }

            trimmed = trimmed.replace(/^- |^\* |^\d+\.\s/, '');

            const match = trimmed.match(/\[Frame\s+(\d+)\]/i);
            let imgBase64 = null;
            
            const isFrameLine = match && (trimmed.includes('Frame & Timestamp') || trimmed.includes('Frame &amp; Timestamp'));

            if (isFrameLine) {
                const paddedNum = String(parseInt(match[1], 10)).padStart(4, '0');
                const imgUrl = `${API.replace(/\/api\/?$/, '')}/api/debug-frames/bug-${bugs[bugIndex].id}/frame_${paddedNum}.png`;
                imgBase64 = await loadImageAsBase64(imgUrl);
                narrowForImage = !!imgBase64;
            }

            const isAnyHeader = trimmed.startsWith('# ') || trimmed.startsWith('## ') || trimmed.startsWith('### ');
            const isSeverityLine = trimmed.toUpperCase().includes('SEVERITY:');

            // Severity or header ends the narrow zone
            if (isSeverityLine || isAnyHeader) {
                narrowForImage = false;
                inFrameZone = !isAnyHeader || /OBSERVED/i.test(trimmed);
                globalImageBottom = 0;
            }

            // Pick text width
            let currentTextW = (narrowForImage || (inFrameZone && cy < globalImageBottom)) ? narrowTextW : fullTextW;

            doc.setLineHeightFactor(LH);
            const dynamicLines = doc.splitTextToSize(trimmed, currentTextW);
            let nodeHeightRequired = dynamicLines.length * 8 * 0.3527 * LH + 5;
            if (imgBase64) nodeHeightRequired = Math.max(nodeHeightRequired, imgH + 6);
            if (isFrameLine) nodeHeightRequired += 8;
            if (isAnyHeader) nodeHeightRequired += 10;

            if (checkSpace(nodeHeightRequired, cy)) {
                cy = y;
                globalImageBottom = 0;
            }

            if (isFrameLine) {
                cy += 4;
                doc.setDrawColor('#e5e7eb');
                doc.setLineWidth(0.2);
                doc.line(cardMargin, cy, w - m - 6, cy);
                cy += 4;
            }

            if (isSeverityLine) {
                const sevMatch = trimmed.match(/SEVERITY:\s*(LOW|MEDIUM|HIGH|CRITICAL)/i);
                const sev = sevMatch ? sevMatch[1].toUpperCase() : 'MEDIUM';
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(8);
                doc.setTextColor('#374151');
                doc.text(`Severity: ${sev}`, cardMargin, cy + 4);
                cy += 8;

            } else if (trimmed.startsWith('# ')) {
                cy += 6;
                doc.setDrawColor('#d1d5db');
                doc.setLineWidth(0.3);
                doc.line(cardMargin, cy, w - m - 6, cy);
                cy += 6;
                const txt = trimmed.replace(/^#+\s+/, '').toUpperCase();
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(11);
                doc.setTextColor('#111827');
                doc.text(txt, cardMargin, cy + 4);
                cy += 10;

            } else if (trimmed.startsWith('## ') || trimmed.startsWith('### ')) {
                cy += 5;
                let txt = trimmed.replace(/^#+\s+/, '');
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(10);
                doc.setTextColor('#111827');
                doc.text(txt, cardMargin, cy + 4);
                cy += 9;

            } else {
                doc.setFont('helvetica', isFrameLine ? 'bold' : 'normal');
                doc.setFontSize(8);
                doc.setTextColor(isFrameLine ? '#111827' : '#374151');

                if (imgBase64) {
                    const imgX = w - m - imgW - 6;
                    const API_BASE = API.replace(/\/api\/?$/, '');
                    const videoUrl = bugs[bugIndex].recording_url?.startsWith('http') ? bugs[bugIndex].recording_url : `${API_BASE}${bugs[bugIndex].recording_url}`;
                    
                    doc.addImage(imgBase64, imgX, cy + 1, imgW, imgH);
                    doc.link(imgX, cy + 1, imgW, imgH, { url: videoUrl });
                    
                    globalImageBottom = cy + 1 + imgH;
                    doc.text(dynamicLines, cardMargin, cy + 3);
                    cy += dynamicLines.length * 8 * 0.3527 * LH + 4;
                } else {
                    doc.text(dynamicLines, cardMargin, cy + 3);
                    cy += dynamicLines.length * 8 * 0.3527 * LH + 4;
                }
            }
        }

        y = cy + 10;
      }

      const totalPages = doc.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        const fY = h - 10;

        doc.setDrawColor('#e5e7eb');
        doc.setLineWidth(0.15);
        doc.line(m, fY - 4, w - m, fY - 4);

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7);
        doc.setTextColor('#9ca3af');
        doc.text("© 2026 BharatQA Security Portal. All audit findings are classified and confidential.", m, fY);
        const pTxt = `Page ${i} of ${totalPages}`;
        doc.text(pTxt, w - m - doc.getTextWidth(pTxt), fY);
      }

      doc.save(`BharatQA-Audit-${(test?.app_name || 'App').replace(/\s+/g, '-')}-${reportDate.replace(/\s+/g, '')}.pdf`);
    } catch (e) {
      console.error(e);
      alert("Failed to generate PDF");
    }
  };

  // --- Render Branches ---

  if (loading) {
    return (
      <div className="shared-load-state">
        <div style={{ textAlign: 'center', zIndex: 1 }}>
          <div className="spinner-glow" />
          <h3 style={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px', letterSpacing: '0.05em', marginTop: '24px', fontWeight: 600 }}>RETRIEVING AUDIT DATA</h3>
        </div>
      </div>
    );
  }

  if (error || !test) {
    return (
      <div className="shared-error-state">
        <div className="error-card">
          <ShieldAlert size={48} color="#F97316" />
          <h2>Audit Unavailable</h2>
          <p>{error || 'This security audit could not be retrieved.'}</p>
          <button onClick={onExit} className="btn-return">Return to Safety</button>
        </div>
      </div>
    );
  }

  // --- Main Content Render ---
  return (
    <div className={`shared-portal-root ${isReady ? 'is-ready' : ''}`}>
      {/* Dynamic Background */}
      <div className="portal-glass-overlay" />

      {/* Navigation Header */}
      <header className="portal-header">
        <div className="header-left">
          <div className="brand-lock">
            {logo && <img src={logo} alt="BharatQA" />}
            <div className="brand-text">BHARAT<span>QA</span></div>
          </div>
          <button className="nav-exit-btn" onClick={onExit}>
            <ArrowLeft size={16} /> Close Portal
          </button>
        </div>
        <div className="header-right">
          <div className="audit-badge">
            <Globe size={14} />
            <span>Pro-level security audit</span>
          </div>
        </div>
      </header>

      <div className="shared-portal-container">
        {/* Hero Area */}
        <main className="portal-main">
          <section className="portal-hero">
            <div className="hero-branding">
              <div className="app-info">
                <div className="audit-label">Official audit report</div>
                <div className="title-row-container" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div className="title-row" style={{ margin: 0 }}>
                    <h1>{test.app_name}</h1>
                    <div className={`status-pill ${test.status === 'active' ? 'active' : 'pending'}`}>
                      {test.status ? test.status.charAt(0).toUpperCase() + test.status.slice(1).toLowerCase() : 'Auditing'}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    {!test.ai_summary && (
                      <button 
                        onClick={handleGenerateSummary} 
                        disabled={generatingSummary}
                        className="download-report-btn" 
                        style={{ background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)', border: 'none' }}
                      >
                        {generatingSummary ? <div className="spinner" style={{ width: 14, height: 14 }} /> : <Brain size={14} />}
                        <span>{generatingSummary ? 'Summarizing...' : 'Generate AI Summary'}</span>
                      </button>
                    )}
                    <button onClick={generatePDF} className="download-report-btn">
                      <Download size={14} /> Download report
                    </button>
                  </div>
                </div>
                <div className="hero-metadata">
                  <div className="company-avatar">
                    {test.company_name ? test.company_name.substring(0, 2).toUpperCase() : 'FU'}
                  </div>
                  <span className="company-tag">{test.company_name}</span>
                  <span className="dot" />
                  <span className="date-tag">{new Date(test.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                </div>

                {test.ai_summary && (
                  <div className="ai-summary-container" style={{
                    marginTop: '24px',
                    padding: '24px',
                    background: 'rgba(255, 255, 255, 0.03)',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    borderRadius: '16px',
                    backdropFilter: 'blur(10px)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', color: '#a855f7' }}>
                      <Brain size={18} />
                      <h3 style={{ margin: 0, fontSize: '14px', textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 700 }}>AI Executive Summary</h3>
                    </div>
                    <div className="ai-summary-content" style={{ color: 'rgba(255,255,255,0.8)', lineHeight: 1.6, fontSize: '15px' }}>
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {test.ai_summary}
                      </ReactMarkdown>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="hero-grid">
              {/* Instructions / Scope */}
              <div className="hero-scope-card">
                <div className="card-lbl">Project scope</div>
                <div className="scope-content">
                  {test.instructions || "No specific instructions were provided for this audit session."}
                </div>
              </div>

              <div className="hero-divider" />

              {/* Requirements Sidebar */}
              <div className="hero-specs-card">
                <div className="card-lbl">Audit targets</div>
                <div className="specs-list">
                  <div className="spec-row">
                    <span className="sl">Device Tier</span>
                    <span className="sv">{test.criteria?.device_tier || 'Global'}</span>
                  </div>
                  {test.criteria?.min_ram_gb && (
                    <div className="spec-row">
                      <span className="sl">RAM Req.</span>
                      <span className="sv">Min {test.criteria.min_ram_gb} GB</span>
                    </div>
                  )}
                  <div className="spec-row">
                    <span className="sl">Network</span>
                    <span className="sv">{(test.criteria?.network_type || 'Any').toUpperCase()}</span>
                  </div>
                  <div className="spec-row">
                    <span className="sl">Location</span>
                    <span className="sv">{test.criteria?.allowed_states || test.criteria?.allowed_cities || 'Anywhere'}</span>
                  </div>
                  <div className="spec-row saffron">
                    <span className="sl">Findings</span>
                    <span className="sv">{bugs.length} Issues</span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Bug Listing */}
          <div className="findings-header">
            <h2>Deep scan results <span className="findings-count">{bugs.length}</span></h2>
            <div className="header-line" />
          </div>

          <div className="findings-grid">
            {bugs.length === 0 ? (
              <div className="empty-findings">
                <div className="empty-icon"><ShieldAlert size={24} /></div>
                <h3>No critical issues found</h3>
                <p>The auditor did not report any bugs for this session.</p>
              </div>
            ) : (
              bugs.map((bug) => {
                const isExpanded = expandedBug === bug.id;
                const stats = safeJsonParse(bug.device_stats);
                const videoUrl = getVideoUrl(bug);
                const notes = extractTesterNotes(bug);
                const suggestedSev = getSuggestedSeverity(bug);
                const isUpgraded = bug.ai_analysis && suggestedSev !== (bug.severity || 'medium').toLowerCase();

                const techSpecs = stats ? [
                  { icon: <Monitor size={14} />, label: 'Model', val: bug.tester_device || pick(stats, ['device_model', 'model', 'deviceModel', 'device']) },
                  { icon: <Code size={14} />, label: 'OS', val: bug.tester_os || pick(stats, ['android_version', 'osVersion', 'os_version', 'androidVersion', 'os', 'release', 'system']) },
                  { icon: <Wifi size={14} />, label: 'Net', val: (bug.tester_net || pick(stats, ['network_type', 'networkType', 'network', 'connection', 'net']) || '').toUpperCase() },
                  { icon: <Smartphone size={14} />, label: 'RAM', val: (bug.tester_ram || pick(stats, ['ram_gb', 'ram', 'memory', 'totalRam', 'total_ram'])) ? `${bug.tester_ram || pick(stats, ['ram_gb', 'ram', 'memory', 'totalRam', 'total_ram'])} GB` : null },
                  { icon: <Tablet size={14} />, label: 'Screen', val: bug.tester_res || pick(stats, ['screen_resolution', 'resolution', 'res']) },
                  { icon: <ShieldAlert size={14} />, label: 'Tier', val: (bug.tester_tier || pick(stats, ['device_tier', 'tier']) || '').toUpperCase() },
                  { icon: <MapPin size={14} />, label: 'Location', val: pick(stats, ['city']) || bug.tester_city },
                  { icon: <Clock size={14} />, label: 'Session', val: bug.test_duration || bug.duration || pick(stats, ['duration_seconds', 'duration', 'session_duration', 'duration_sec']) ? `${bug.test_duration || bug.duration || pick(stats, ['duration_seconds', 'duration', 'session_duration', 'duration_sec'])}s` : null },
                  { icon: <Wifi size={14} />, label: 'Speed', val: pick(stats, ['networkSpeed', 'network_speed']) },
                  { icon: <Wifi size={14} />, label: 'Net Drop', val: pick(stats, ['didNetworkDropDuringTest']) ? 'Yes ⚠️' : (pick(stats, ['didNetworkDropDuringTest']) === false ? 'No' : null) },
                  { icon: <ShieldAlert size={14} />, label: 'Thermal', val: pick(stats, ['highestThermalState']) },
                  { icon: <Smartphone size={14} />, label: 'Free RAM', val: pick(stats, ['availableMemoryMb']) ? `${pick(stats, ['availableMemoryMb'])} MB` : null },
                  { icon: <Smartphone size={14} />, label: 'Storage', val: pick(stats, ['availableStorageMb']) ? `${pick(stats, ['availableStorageMb'])} MB` : null },
                  { icon: <Battery size={14} />, label: 'Pwr Saver', val: pick(stats, ['isPowerSaveMode']) ? 'On' : (pick(stats, ['isPowerSaveMode']) === false ? 'Off' : null) }
                ].filter(s => s.val && s.val !== 'S') : [];

                return (
                  <div key={bug.id} className={`finding-card ${isExpanded ? 'active' : ''}`}>
                    <div className="card-tgl" onClick={() => toggleExpand(bug.id)}>
                      <div className="card-tgl-left">
                        <div className="bug-basics">
                          <h3>{bug.title || bug.bug_title || 'Internal Finding'}</h3>
                          <div className="bug-meta-pills">
                            <span className="pill-tester">BharatQA Auditor</span>
                          </div>
                        </div>
                      </div>
                      <div className="card-tgl-right">
                        <span className={`pill-severity ${suggestedSev}`}>
                          {isUpgraded && <Brain size={10} style={{ marginRight: 4 }} />}
                          {suggestedSev.charAt(0).toUpperCase() + suggestedSev.slice(1).toLowerCase()}
                        </span>
                        <div className="tgl-icon">
                          <ChevronDown size={20} />
                        </div>
                      </div>
                    </div>

                    <div className="card-expanded-body" style={{ display: isExpanded ? 'block' : 'none' }}>
                      <div className="expanded-layout">
                        {/* Left Pane: Notes & AI */}
                        <div className="exp-pane pane-left">
                          <div className="pane-sect">
                            <div className="sect-lbl">Auditor notes</div>
                            <div className="sect-box notes">
                              <div className="notes-dot" />
                              <div className="notes-text">{notes || 'Detailed finding logs provided below in the technical report.'}</div>
                            </div>
                          </div>

                          <div className="pane-sect">
                            <div className="sect-lbl">AI technical verdict</div>
                            <div className="sect-box ai-wrap">
                              {bug.ai_analysis ? (
                                <div className="markdown-ai">
                                  <ReactMarkdown
                                    remarkPlugins={[remarkGfm]}
                                    components={{
                                      h1: ({ node, ...props }) => {
                                        const text = String(props.children);
                                        if (text.toUpperCase().includes('SEVERITY:')) {
                                          const sev = text.split(':')[1]?.trim() || '';
                                          return (
                                            <div className="markdown-severity-row">
                                              <span className="sect-lbl">Severity</span>
                                              <span className={`pill-severity ${sev.toLowerCase()}`}>
                                                {sev.charAt(0).toUpperCase() + sev.slice(1).toLowerCase()}
                                              </span>
                                            </div>
                                          );
                                        }
                                        return <h1 {...props} />;
                                      },
                                      h2: ({ node, ...props }) => {
                                        const t = String(props.children).toUpperCase();
                                        if (t.includes('BUG REPRODUCTION STEPS')) return <h2>Bug reproduction steps</h2>;
                                        if (t.includes('ROOT CAUSE')) return <h2>Technical root cause</h2>;
                                        if (t.includes('TOP 5 FIXES') || t.includes('FIXES')) return <h2>Top 5 fixes</h2>;
                                        return <h2 {...props} />;
                                      },
                                      h3: ({ node, ...props }) => {
                                        const t = String(props.children).toUpperCase();
                                        if (t.includes('BUG REPRODUCTION STEPS')) return <h3>Bug reproduction steps</h3>;
                                        if (t.includes('ROOT CAUSE')) return <h3>Technical root cause</h3>;
                                        if (t.includes('TOP 5 FIXES') || t.includes('FIXES')) return <h3>Top 5 fixes</h3>;
                                        return <h3 {...props} />;
                                      },
                                      ul: ({ node, ...props }) => <ul className="ai-list" {...props} />,
                                      ol: ({ node, ...props }) => <ol className="ai-steps" {...props} />
                                    }}
                                  >
                                    {cleanAIReport(bug.ai_analysis)}
                                  </ReactMarkdown>
                                </div>
                              ) : (
                                <div className="no-ai">Standard security analysis performed.</div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Right Pane: Media & Specs */}
                        <div className="exp-pane pane-right">
                          <div className="pane-sect">
                            <div className="sect-lbl">Visual evidence</div>
                            <div className="media-box">
                              {videoUrl ? (
                                <AuthorizedVideo url={videoUrl} title={`evidence-${bug.id}`} />
                              ) : (
                                <div className="no-video">
                                  <Video size={24} opacity={0.3} />
                                  <span>No visual recording available</span>
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="pane-sect">
                            <div className="sect-lbl standout">Session telemetry</div>
                            <div className="telemetry-grid">
                              {techSpecs.map((s, idx) => {
                                if (s.label === 'Net') {
                                  return (
                                    <div key={idx} className="tel-item-net">
                                      <div className="tel-net-left">
                                        <div className="tel-net-dot" />
                                        <span className="tel-lbl">Net</span>
                                      </div>
                                      <span className="tel-val">{s.val}</span>
                                    </div>
                                  );
                                }
                                return (
                                  <div key={idx} className="tel-item">
                                    <div className="tel-icon">{s.icon}</div>
                                    <div className="tel-info">
                                      <span className="tel-lbl">{s.label}</span>
                                      <span className="tel-val">{s.val}</span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </main>
      </div>

      {/* State Overlay (Removed for production, keeping basic for check) */}
      <footer className="shared-portal-footer">
        © {new Date().getFullYear()} BharatQA Security Portal. All Audit findings are classified and confidential.
      </footer>
    </div>
  );
}
